import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  AiOutlineCustomerService,
  AiOutlineMessage,
  AiOutlineShoppingCart,
} from "react-icons/ai";
import Nav from "../../components/home/Nav";
import AccountList from "../../components/home/AccountList";
import { useSelector } from "react-redux";
import { useGetOrdersQuery, useCreateSupportMessageMutation } from "../../store/services/userOrdersService";
import { toPersianNumber } from "../../utils/persian";

const subjects = [
  "پیگیری سفارش",
  "تعویض یا مرجوعی",
  "مشکل پرداخت",
  "مشاوره سایز",
  "سایر موارد",
];

const formatDate = (value) => {
  if (!value) return "";

  try {
    return new Date(value).toLocaleDateString("fa-IR");
  } catch {
    return "";
  }
};

const normalizeMobile = (value) => String(value || "").replace(/[^\d]/g, "");

const UserSupport = () => {
  const { user } = useSelector((state) => state.authReducer);
  const { data: ordersData, isFetching: ordersLoading } = useGetOrdersQuery(1);
  const [createSupportMessage, { isLoading }] = useCreateSupportMessageMutation();

  const orders = ordersData?.orders?.data || [];

  const initialForm = useMemo(() => ({
    full_name: user?.name && user.name !== "کاربر جوتی" ? user.name : "",
    mobile: user?.mobile || "",
    email: "",
    subject: "پیگیری سفارش",
    order_id: "",
    message: "",
  }), [user?.name, user?.mobile]);

  const [form, setForm] = useState(initialForm);

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const payload = {
      ...form,
      mobile: normalizeMobile(form.mobile),
      order_id: form.order_id ? Number(form.order_id) : null,
      source: "profile",
    };

    try {
      const response = await createSupportMessage(payload).unwrap();
      toast.success(response?.message || "درخواست شما ثبت شد.");
      setForm({
        ...initialForm,
        subject: form.subject,
        order_id: "",
        message: "",
        email: "",
      });
    } catch (error) {
      const errors = error?.data?.errors;
      const firstError = errors ? Object.values(errors)?.[0]?.[0] : null;
      toast.error(firstError || error?.data?.message || "ثبت درخواست ناموفق بود.");
    }
  };

  return (
    <>
      <Nav />
      <Toaster position="top-right" reverseOrder={false} />

      <main className="account-page">
        <div className="my-container">
          <div className="account-shell">
            <AccountList />

            <section className="account-content">
              <div className="account-hero account-hero-compact">
                <div>
                  <span className="account-kicker">پشتیبانی جوتی</span>
                  <h1>ثبت درخواست پشتیبانی</h1>
                  <p>اگر درباره سفارش، پرداخت، ارسال، تعویض یا سایز سوالی دارید، از همین بخش پیام بفرستید.</p>
                </div>

                <Link to="/orders" className="account-hero-action">
                  سفارش‌های من
                </Link>
              </div>

              <section className="account-card support-card">
                <div className="account-card-head">
                  <div>
                    <span>فرم تماس با پشتیبانی</span>
                    <h2>پیام جدید</h2>
                  </div>
                  <AiOutlineCustomerService size={27} />
                </div>

                <form className="account-form support-form" onSubmit={submitForm}>
                  <label>
                    <span>نام و نام خانوادگی</span>
                    <input
                      type="text"
                      value={form.full_name}
                      onChange={(event) => updateField("full_name", event.target.value)}
                      placeholder="مثلاً مهسا احمدی"
                      required
                    />
                  </label>

                  <label>
                    <span>موبایل</span>
                    <input
                      type="tel"
                      value={form.mobile}
                      onChange={(event) => updateField("mobile", event.target.value)}
                      placeholder="09123456789"
                      required
                    />
                  </label>

                  <label>
                    <span>ایمیل، اختیاری</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => updateField("email", event.target.value)}
                      placeholder="example@email.com"
                    />
                  </label>

                  <label>
                    <span>موضوع درخواست</span>
                    <select
                      value={form.subject}
                      onChange={(event) => updateField("subject", event.target.value)}
                      required
                    >
                      {subjects.map((subject) => (
                        <option value={subject} key={subject}>
                          {subject}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="account-field-wide">
                    <span>سفارش مرتبط، اختیاری</span>
                    {ordersLoading ? (
                      <div className="support-order-loading">در حال دریافت سفارش‌ها...</div>
                    ) : (
                      <select
                        value={form.order_id}
                        onChange={(event) => updateField("order_id", event.target.value)}
                      >
                        <option value="">بدون انتخاب سفارش</option>
                        {orders.map((order) => (
                          <option value={order.id} key={order.id}>
                            {order.order_number || `سفارش ${toPersianNumber(order.id)}`} - {formatDate(order.created_at)}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>

                  <label className="account-field-wide">
                    <span>متن پیام</span>
                    <textarea
                      value={form.message}
                      onChange={(event) => updateField("message", event.target.value)}
                      placeholder="لطفاً موضوع را واضح بنویسید تا پشتیبانی سریع‌تر بررسی کند."
                      required
                    />
                  </label>

                  <div className="account-form-actions">
                    <button type="submit" disabled={isLoading}>
                      {isLoading ? "در حال ثبت..." : "ثبت درخواست"}
                    </button>
                  </div>
                </form>
              </section>

              <section className="support-help-grid">
                <article>
                  <AiOutlineShoppingCart size={24} />
                  <strong>درخواست مرتبط با سفارش</strong>
                  <p>برای پیگیری ارسال، پرداخت، تعویض یا مرجوعی، سفارش مربوطه را انتخاب کنید.</p>
                </article>

                <article>
                  <AiOutlineMessage size={24} />
                  <strong>پاسخ پشتیبانی</strong>
                  <p>پشتیبانی جوتی درخواست شما را بررسی می‌کند و از طریق تماس یا پیامک پیگیری انجام می‌شود.</p>
                </article>
              </section>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserSupport;
